<template>
  <div class="wishlist-view">
    <!-- Header med navigering och kontroller -->
    <header class="wishlist-header">
      <div class="header-content">
        <div class="navigation">
          <h1 v-if="!selectedUser">Önskelistor</h1>
          <div v-else class="breadcrumb">
            <button @click="goBackToUserList" class="back-btn">
              ← Tillbaka till listan
            </button>
            <h1>{{ selectedUser.name }}s önskelista</h1>
          </div>
        </div>
        
        <div class="header-controls">
          <div v-if="!isAuthenticated" class="auth-controls">
            <button @click="openLogin" class="login-btn">
              Logga in
            </button>
            <button v-if="registrationOpen" @click="showRegister" class="register-btn">
              <span class="plus-icon">+</span>
              Registrera dig
            </button>
          </div>

          <div v-else class="user-controls">
            <span class="welcome-text">Välkommen, {{ currentUser?.name }}!</span>
            <button v-if="isAdmin" @click="showAdmin = true" class="admin-btn">
              ⚙️ Admin
            </button>
            <button @click="openMyWishlist" class="my-wishlist-btn">
              🎁 Min önskelista
            </button>
            <button @click="handleLogout" class="logout-btn">
              Logga ut
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Huvudinnehåll -->
    <main class="wishlist-main">
      <!-- Laddar -->
      <div v-if="loading" class="loading">
        <p>⏳ Laddar...</p>
      </div>

      <!-- Fel -->
      <div v-else-if="error" class="error-message">
        <p>❌ {{ error }}</p>
        <button @click="loadUsers" class="retry-btn">Försök igen</button>
      </div>

      <!-- Användarlista -->
      <div v-else-if="!selectedUser" class="users-list">
        <div class="users-grid">
          <div
            v-for="user in users"
            :key="user.id"
            @click="selectUser(user)"
            class="user-card"
          >
            <div class="user-avatar" :style="{ background: avatarGradient(user.name) }">
              {{ initials(user.name) }}
            </div>
            <div class="user-info">
              <h3>{{ user.name }}</h3>
              <div class="wishlist-stats">
                <span class="item-count">
                  🎁 {{ user.wishlist.length }} önskemål
                </span>
              </div>
            </div>
            <span class="card-arrow" aria-hidden="true">→</span>
          </div>
        </div>
      </div>

      <!-- Vald användares önskelista -->
      <div v-else class="user-wishlist">
        <div v-if="selectedUser.wishlist.length === 0" class="empty-wishlist">
          <h3>{{ selectedUser.name }} har ingen önskelista än</h3>
          <p>Denna användare har inte lagt till några önskemål än.</p>
        </div>
        
        <div v-else class="wishlist-content">
          <div class="wishlist-controls">
            <label for="sortBy">Sortera efter:</label>
            <select id="sortBy" v-model="sortBy" class="sort-select">
              <option value="priority">Prioritet (hög först)</option>
              <option value="priority-desc">Prioritet (låg först)</option>
              <option value="name">Namn (A-Ö)</option>
              <option value="name-desc">Namn (Ö-A)</option>
              <option value="price">Pris (låg först)</option>
              <option value="price-desc">Pris (hög först)</option>
            </select>
          </div>
          
          <div class="items-list">
            <WishListItem
              v-for="item in sortedWishlist"
              :key="item.id"
              :item="item"
              :currentUserId="currentUser?.id"
              :isOwnItem="false"
              :isAuthenticated="isAuthenticated"
              @assign="handleAssign"
              @updateAssignment="handleUpdateAssignment"
              @removeAssignment="handleRemoveAssignment"
            />
          </div>
        </div>
      </div>
    </main>

    <!-- Min önskelista popup -->
    <div v-if="showMyWishlist" class="modal-overlay" @click="closeMyWishlist">
      <div class="my-wishlist-modal" @click.stop>
        <div class="modal-header">
          <h2>Min önskelista</h2>
          <button @click="closeMyWishlist" class="close-btn">×</button>
        </div>
        
        <div class="modal-content">
          <div class="modal-controls">
            <button @click="showAddModal = true" class="add-item-btn">
              <span class="plus-icon">+</span>
              Lägg till önskeobjekt
            </button>
          </div>

          <div v-if="myWishlistLoading" class="loading">
            <p>⏳ Laddar din önskelista...</p>
          </div>
          
          <div v-else-if="myWishlistError" class="error-message">
            <p>❌ {{ myWishlistError }}</p>
            <button @click="loadMyWishlist" class="retry-btn">Försök igen</button>
          </div>
          
          <div v-else-if="myWishlist.length === 0" class="empty-wishlist">
            <h3>Din önskelista är tom</h3>
            <p>Lägg till ditt första önskeobjekt!</p>
          </div>
          
          <div v-else class="items-list">
            <WishListItem
              v-for="item in sortedMyWishlist"
              :key="item.id"
              :item="item"
              :currentUserId="currentUser?.id"
              :isOwnItem="true"
              :isAuthenticated="isAuthenticated"
              @edit="handleEdit"
              @delete="handleDelete"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Modaler -->
    <LoginComponent
      v-if="showLogin"
      :startInRegister="loginStartRegister"
      @loginSuccess="handleLoginSuccess"
      @close="showLogin = false"
    />

    <AdminPanel
      v-if="showAdmin"
      @close="showAdmin = false"
    />
    
    <AddItemModal
      :show="showAddModal"
      :editItem="editingItem"
      @close="closeAddModal"
      @itemAdded="handleItemAdded"
      @itemUpdated="handleItemUpdated"
    />

    <!-- Assignment modal -->
    <AssignmentModal
      v-if="showAssignmentModal"
      :item="assignmentItem"
      :existingAssignment="existingAssignment"
      @close="closeAssignmentModal"
      @assigned="handleAssignmentSuccess"
      @updated="handleAssignmentUpdated"
      @removed="handleAssignmentRemoved"
    />
  </div>
</template>

<script>
import WishListItem from '@/components/WishListItem.vue';
import LoginComponent from '@/components/LoginComponent.vue';
import AddItemModal from '@/components/AddItemModal.vue';
import AssignmentModal from '@/components/AssignmentModal.vue';
import AdminPanel from '@/components/AdminPanel.vue';
import apiService from '@/services/api.js';
import authService from '@/services/auth.js';

export default {
  name: 'WishListView',
  components: {
    WishListItem,
    LoginComponent,
    AddItemModal,
    AssignmentModal,
    AdminPanel
  },
  data() {
    return {
      isAuthenticated: false,
      isAdmin: false,
      currentUser: null,
      users: [],
      selectedUser: null,
      myWishlist: [],
      loading: false,
      myWishlistLoading: false,
      error: null,
      myWishlistError: null,
      showLogin: false,
      loginStartRegister: false,
      showAdmin: false,
      registrationOpen: true,
      showMyWishlist: false,
      showAddModal: false,
      showAssignmentModal: false,
      editingItem: null,
      assignmentItem: null,
      existingAssignment: null,
      sortBy: 'priority'
    };
  },
  computed: {
    sortedWishlist() {
      if (!this.selectedUser?.wishlist) return [];
      return this.sortItems(this.selectedUser.wishlist);
    },
    
    sortedMyWishlist() {
      return this.sortItems(this.myWishlist);
    }
  },
  async mounted() {
    this.checkAuthentication();
    this.loadConfig();
    await this.loadUsers();
  },
  methods: {
    checkAuthentication() {
      this.isAuthenticated = authService.validateAndCleanupToken();
      this.isAdmin = this.isAuthenticated && authService.isAdmin();
      if (this.isAuthenticated) {
        this.loadCurrentUser();
      }
    },

    async loadConfig() {
      try {
        const cfg = await apiService.getPublicConfig();
        this.registrationOpen = cfg.registrationOpen !== false;
      } catch (e) {
        this.registrationOpen = true;
      }
    },

    async loadCurrentUser() {
      try {
        const token = authService.getToken();
        const result = await apiService.getProfile(token);
        this.currentUser = result.me;
      } catch (error) {
        console.error('Failed to load current user:', error);
        this.handleLogout();
      }
    },

    async loadUsers() {
      this.loading = true;
      this.error = null;
      
      try {
        const token = this.isAuthenticated ? authService.getToken() : null;
        const result = await apiService.getAllUsers(token);
        this.users = result.users || [];
      } catch (error) {
        console.error('Failed to load users:', error);
        this.error = error.message || 'Kunde inte ladda användare';
      } finally {
        this.loading = false;
      }
    },

    async loadMyWishlist() {
      if (!this.isAuthenticated) return;
      
      this.myWishlistLoading = true;
      this.myWishlistError = null;
      
      try {
        const token = authService.getToken();
        const result = await apiService.getWishlist(token);
        this.myWishlist = result.wishlist || [];
      } catch (error) {
        console.error('Failed to load my wishlist:', error);
        this.myWishlistError = error.message || 'Kunde inte ladda din önskelista';
        
        if (error.message.includes('unauthorized') || error.message.includes('authentication')) {
          this.handleLogout();
        }
      } finally {
        this.myWishlistLoading = false;
      }
    },

    selectUser(user) {
      this.selectedUser = user;
    },

    goBackToUserList() {
      this.selectedUser = null;
    },

    openLogin() {
      this.loginStartRegister = false;
      this.showLogin = true;
    },

    showRegister() {
      this.loginStartRegister = true;
      this.showLogin = true;
    },

    handleLoginSuccess() {
      this.showLogin = false;
      this.isAuthenticated = true;
      this.isAdmin = authService.isAdmin();
      this.loadCurrentUser();
      this.loadUsers(); // Reload to get assignments
    },

    handleLogout() {
      authService.logout();
      this.isAuthenticated = false;
      this.isAdmin = false;
      this.showAdmin = false;
      this.currentUser = null;
      this.myWishlist = [];
      this.showMyWishlist = false;
      this.loadUsers(); // Reload without assignments
    },

    closeMyWishlist() {
      this.showMyWishlist = false;
    },

    async openMyWishlist() {
      this.showMyWishlist = true;
      await this.loadMyWishlist();
    },

    // Item management methods
    handleEdit(item) {
      this.editingItem = item;
      this.showAddModal = true;
    },

    closeAddModal() {
      this.showAddModal = false;
      this.editingItem = null;
    },

    handleItemAdded(newItem) {
      this.myWishlist.push(newItem);
    },

    handleItemUpdated(updatedItem) {
      const index = this.myWishlist.findIndex(item => item.id === updatedItem.id);
      if (index !== -1) {
        this.myWishlist.splice(index, 1, updatedItem);
      }
    },

    handleDelete(itemId) {
      this.myWishlist = this.myWishlist.filter(item => item.id !== itemId);
    },

    // Assignment methods
    handleAssign(item) {
      this.assignmentItem = item;
      this.existingAssignment = null;
      
      // Check if user is already assigned
      const userAssignment = item.assignments?.find(
        assignment => assignment.assignee.id === this.currentUser.id
      );
      
      if (userAssignment) {
        this.existingAssignment = userAssignment;
      }
      
      this.showAssignmentModal = true;
    },

    handleUpdateAssignment(assignment) {
      this.assignmentItem = null;
      this.existingAssignment = assignment;
      this.showAssignmentModal = true;
    },

    async handleRemoveAssignment(assignmentId) {
      try {
        const token = authService.getToken();
        await apiService.removeAssignment(assignmentId, token);
        
        // Update the selected user's wishlist
        if (this.selectedUser) {
          this.selectedUser.wishlist.forEach(item => {
            item.assignments = item.assignments.filter(a => a.id !== assignmentId);
          });
        }
        
        // Reload users to get updated data
        await this.loadUsers();
      } catch (error) {
        alert('Kunde inte ta bort assignment: ' + error.message);
      }
    },

    closeAssignmentModal() {
      this.showAssignmentModal = false;
      this.assignmentItem = null;
      this.existingAssignment = null;
    },

    async handleAssignmentSuccess() {
      this.closeAssignmentModal();
      await this.loadUsers(); // Reload to get updated assignments
    },

    async handleAssignmentUpdated() {
      this.closeAssignmentModal();
      await this.loadUsers(); // Reload to get updated assignments
    },

    async handleAssignmentRemoved() {
      this.closeAssignmentModal();
      await this.loadUsers(); // Reload to get updated assignments
    },

    // Utility methods
    sortItems(items) {
      const sorted = [...items];
      
      switch (this.sortBy) {
        case 'priority':
          return sorted.sort((a, b) => a.priority - b.priority);
        case 'priority-desc':
          return sorted.sort((a, b) => b.priority - a.priority);
        case 'name':
          return sorted.sort((a, b) => a.name.localeCompare(b.name, 'sv'));
        case 'name-desc':
          return sorted.sort((a, b) => b.name.localeCompare(a.name, 'sv'));
        case 'price':
          return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
        case 'price-desc':
          return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
        default:
          return sorted;
      }
    },

    // Presentation: initialer och en stabil gradient-avatar per namn
    initials(name) {
      if (!name) return '?';
      const parts = name.trim().split(/\s+/);
      const first = parts[0]?.[0] || '';
      const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
      return (first + last).toUpperCase();
    },

    avatarGradient(name) {
      let hash = 0;
      for (let i = 0; i < (name || '').length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      const h1 = Math.abs(hash) % 360;
      const h2 = (h1 + 40) % 360;
      return `linear-gradient(135deg, hsl(${h1} 65% 55%), hsl(${h2} 70% 45%))`;
    }
  },
  watch: {
    showMyWishlist(newVal) {
      if (newVal && this.isAuthenticated) {
        this.loadMyWishlist();
      }
    }
  }
};
</script>

<style scoped>
.wishlist-view {
  /* Lokalt designsystem för önskelistesidan */
  --wl-primary: #2e7d32;
  --wl-primary-dark: #1b5e20;
  --wl-primary-soft: #e8f3e9;
  --wl-accent: #e8890c;
  --wl-bg: #f4f6f8;
  --wl-surface: #ffffff;
  --wl-text: #1f2933;
  --wl-muted: #6b7684;
  --wl-border: #e6e9ee;
  --wl-radius: 16px;
  --wl-radius-sm: 10px;
  --wl-shadow: 0 1px 3px rgba(16, 24, 40, 0.06), 0 1px 2px rgba(16, 24, 40, 0.04);
  --wl-shadow-lg: 0 12px 28px rgba(16, 24, 40, 0.12);

  min-height: 100vh;
  background:
    radial-gradient(1100px 500px at 100% -10%, rgba(46, 125, 50, 0.08), transparent 60%),
    radial-gradient(900px 450px at -10% 0%, rgba(232, 137, 12, 0.07), transparent 55%),
    var(--wl-bg);
  color: var(--wl-text);
}

.wishlist-header {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: saturate(180%) blur(12px);
  -webkit-backdrop-filter: saturate(180%) blur(12px);
  border-bottom: 1px solid var(--wl-border);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  max-width: 1120px;
  margin: 0 auto;
  padding: 1.25rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.navigation h1 {
  margin: 0;
  color: var(--wl-text);
  font-size: 1.9rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.back-btn {
  padding: 0.55rem 1rem;
  background: var(--wl-surface);
  border: 1px solid var(--wl-border);
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  color: var(--wl-text);
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: var(--wl-primary-soft);
  border-color: var(--wl-primary);
  color: var(--wl-primary-dark);
}

.auth-controls,
.user-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.welcome-text {
  color: var(--wl-primary-dark);
  font-weight: 600;
  background: var(--wl-primary-soft);
  padding: 0.5rem 0.9rem;
  border-radius: 999px;
  font-size: 0.95rem;
}

.login-btn,
.logout-btn,
.register-btn,
.my-wishlist-btn,
.retry-btn {
  padding: 0.7rem 1.35rem;
  border: 1px solid transparent;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.login-btn:hover,
.register-btn:hover,
.my-wishlist-btn:hover,
.retry-btn:hover {
  transform: translateY(-1px);
}

.login-btn {
  background: linear-gradient(135deg, var(--wl-primary), var(--wl-primary-dark));
  color: #fff;
  box-shadow: 0 4px 12px rgba(46, 125, 50, 0.28);
}

.login-btn:hover {
  box-shadow: 0 6px 18px rgba(46, 125, 50, 0.38);
}

.register-btn {
  background: var(--wl-surface);
  color: var(--wl-primary-dark);
  border-color: var(--wl-primary);
}

.register-btn:hover {
  background: var(--wl-primary-soft);
}

.my-wishlist-btn {
  background: linear-gradient(135deg, #f0a020, var(--wl-accent));
  color: #fff;
  box-shadow: 0 4px 12px rgba(232, 137, 12, 0.28);
}

.my-wishlist-btn:hover {
  box-shadow: 0 6px 18px rgba(232, 137, 12, 0.38);
}

.logout-btn {
  background: transparent;
  color: var(--wl-muted);
  border-color: var(--wl-border);
}

.logout-btn:hover {
  background: #f2f4f7;
  color: var(--wl-text);
}

.admin-btn {
  background: var(--wl-surface);
  color: var(--wl-primary-dark);
  border-color: var(--wl-primary);
}

.admin-btn:hover {
  background: var(--wl-primary-soft);
  transform: translateY(-1px);
}

.plus-icon {
  font-size: 1.15rem;
  font-weight: bold;
  line-height: 1;
}

.wishlist-main {
  max-width: 1120px;
  margin: 0 auto;
  padding: 2.25rem 2rem 3rem;
}

.loading,
.error-message {
  text-align: center;
  padding: 3rem 2rem;
  background: var(--wl-surface);
  border: 1px solid var(--wl-border);
  border-radius: var(--wl-radius);
  box-shadow: var(--wl-shadow);
}

.loading p {
  font-size: 1.15rem;
  color: var(--wl-muted);
  margin: 0;
}

.error-message p {
  color: #d92d20;
  margin-bottom: 1rem;
  font-weight: 600;
}

.retry-btn {
  background: linear-gradient(135deg, var(--wl-primary), var(--wl-primary-dark));
  color: #fff;
}

.users-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.25rem;
}

.user-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: var(--wl-surface);
  border: 1px solid var(--wl-border);
  border-radius: var(--wl-radius);
  padding: 1.25rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  box-shadow: var(--wl-shadow);
}

.user-card:hover {
  border-color: rgba(46, 125, 50, 0.35);
  box-shadow: var(--wl-shadow-lg);
  transform: translateY(-3px);
}

.user-avatar {
  flex: 0 0 auto;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  font-size: 1.15rem;
  letter-spacing: 0.02em;
  box-shadow: inset 0 0 0 3px rgba(255, 255, 255, 0.25);
  user-select: none;
}

.user-info {
  flex: 1 1 auto;
  min-width: 0;
}

.user-info h3 {
  margin: 0 0 0.4rem 0;
  color: var(--wl-text);
  font-size: 1.2rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wishlist-stats {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.item-count {
  background: var(--wl-primary-soft);
  padding: 0.28rem 0.75rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--wl-primary-dark);
}

.card-arrow {
  flex: 0 0 auto;
  color: var(--wl-muted);
  font-size: 1.3rem;
  transition: transform 0.2s ease, color 0.2s ease;
}

.user-card:hover .card-arrow {
  transform: translateX(4px);
  color: var(--wl-primary);
}

.user-wishlist {
  background: var(--wl-surface);
  border: 1px solid var(--wl-border);
  border-radius: var(--wl-radius);
  box-shadow: var(--wl-shadow);
  overflow: hidden;
}

.empty-wishlist {
  text-align: center;
  padding: 3.5rem 2rem;
}

.empty-wishlist h3 {
  color: var(--wl-text);
  margin-bottom: 0.75rem;
  font-size: 1.3rem;
}

.empty-wishlist p {
  color: var(--wl-muted);
}

.wishlist-content {
  padding: 2rem;
}

.wishlist-controls {
  background: #f7f9fb;
  border: 1px solid var(--wl-border);
  padding: 0.9rem 1.25rem;
  border-radius: 999px;
  margin-bottom: 1.75rem;
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.wishlist-controls label {
  font-weight: 600;
  color: var(--wl-muted);
}

.sort-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--wl-border);
  border-radius: 999px;
  font-size: 0.95rem;
  background: var(--wl-surface);
  color: var(--wl-text);
  cursor: pointer;
}

.sort-select:focus {
  outline: none;
  border-color: var(--wl-primary);
  box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.15);
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.my-wishlist-modal {
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.my-wishlist-modal {
  border: 1px solid var(--wl-border);
}

.modal-header {
  background: linear-gradient(135deg, var(--wl-primary), var(--wl-primary-dark));
  padding: 1.35rem 2rem;
  border-bottom: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  color: #fff;
  font-weight: 700;
}

.modal-header .close-btn {
  color: rgba(255, 255, 255, 0.9);
}

.modal-header .close-btn:hover {
  background: rgba(255, 255, 255, 0.18);
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0.5rem;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: #e9ecef;
}

.modal-content {
  padding: 2rem;
  overflow-y: auto;
  flex: 1;
}

.modal-controls {
  margin-bottom: 2rem;
}

.add-item-btn {
  background: linear-gradient(135deg, var(--wl-primary), var(--wl-primary-dark));
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 12px rgba(46, 125, 50, 0.28);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.add-item-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(46, 125, 50, 0.38);
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
  }
  
  .header-content h1 {
    font-size: 1.5rem;
  }

  .breadcrumb {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }

  .breadcrumb h1 {
    font-size: 1.25rem;
    order: 2;
  }

  .back-btn {
    order: 1;
    align-self: center;
    min-height: 48px;
    padding: 0.75rem 1.25rem;
    font-size: 1rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  .auth-controls,
  .user-controls {
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.75rem;
  }

  .login-btn,
  .logout-btn,
  .register-btn,
  .my-wishlist-btn,
  .retry-btn {
    min-height: 48px;
    padding: 0.875rem 1.25rem;
    font-size: 1rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .welcome-text {
    width: 100%;
    text-align: center;
    margin-bottom: 0.5rem;
    order: -1;
  }
  
  .wishlist-main {
    padding: 1rem;
  }
  
  .users-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .user-card {
    padding: 1.25rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .user-card:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
  }
  
  .wishlist-controls {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
    padding: 1.25rem;
  }

  .wishlist-controls label {
    font-size: 1rem;
  }

  .sort-select {
    padding: 0.75rem;
    font-size: 1rem;
    min-height: 48px;
    touch-action: manipulation;
  }
  
  .my-wishlist-modal {
    width: 95%;
    max-height: 95vh;
    margin: 1rem;
  }
  
  .modal-content {
    padding: 1rem;
  }

  .modal-header {
    padding: 1rem 1.25rem;
  }

  .modal-header h2 {
    font-size: 1.25rem;
  }

  .close-btn {
    width: 48px;
    height: 48px;
    font-size: 1.5rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .add-item-btn {
    width: 100%;
    justify-content: center;
    min-height: 48px;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .loading,
  .error-message {
    padding: 2rem 1rem;
    margin: 1rem;
  }

  .loading p {
    font-size: 1.1rem;
  }

  .error-message p {
    font-size: 1rem;
    margin-bottom: 1.25rem;
  }

  .empty-wishlist {
    padding: 2rem 1rem;
  }

  .empty-wishlist h3 {
    font-size: 1.1rem;
  }
}

@media (max-width: 480px) {
  .header-content {
    padding: 0.75rem;
  }

  .header-content h1 {
    font-size: 1.25rem;
  }

  .breadcrumb h1 {
    font-size: 1.1rem;
  }

  .back-btn {
    padding: 0.625rem 1rem;
    font-size: 0.9rem;
  }

  .auth-controls,
  .user-controls {
    gap: 0.5rem;
  }

  .login-btn,
  .logout-btn,
  .register-btn,
  .my-wishlist-btn,
  .retry-btn {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }

  .welcome-text {
    font-size: 0.9rem;
  }

  .wishlist-main {
    padding: 0.75rem;
  }

  .users-grid {
    gap: 0.75rem;
  }

  .user-card {
    padding: 1rem;
  }

  .user-info h3 {
    font-size: 1.1rem;
  }

  .user-info p {
    font-size: 0.9rem;
  }

  .item-count {
    font-size: 0.8rem;
    padding: 0.2rem 0.6rem;
  }

  .wishlist-controls {
    padding: 1rem;
  }

  .sort-select {
    padding: 0.625rem;
    font-size: 0.9rem;
  }

  .my-wishlist-modal {
    width: 98%;
    margin: 0.5rem;
  }

  .modal-header {
    padding: 0.875rem 1rem;
  }

  .modal-header h2 {
    font-size: 1.1rem;
  }

  .close-btn {
    width: 44px;
    height: 44px;
    font-size: 1.25rem;
  }

  .modal-content {
    padding: 0.875rem;
  }

  .add-item-btn {
    padding: 0.875rem 1.25rem;
    font-size: 0.9rem;
  }

  .loading,
  .error-message {
    padding: 1.5rem 0.75rem;
    margin: 0.75rem;
  }

  .loading p {
    font-size: 1rem;
  }

  .empty-wishlist {
    padding: 1.5rem 0.75rem;
  }

  .empty-wishlist h3 {
    font-size: 1rem;
  }

  .empty-wishlist p {
    font-size: 0.9rem;
  }
}

/* Touch device specific improvements */
@media (hover: none) and (pointer: coarse) {
  .user-card:hover {
    border-color: #e9ecef;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: none;
  }

  .login-btn:hover,
  .logout-btn:hover,
  .register-btn:hover,
  .my-wishlist-btn:hover,
  .retry-btn:hover,
  .add-item-btn:hover,
  .back-btn:hover,
  .close-btn:hover {
    transform: none;
  }

  /* Add visual feedback for taps */
  .user-card:active {
    background-color: #f8f9fa;
  }

  .login-btn:active {
    background-color: #0056b3;
  }

  .register-btn:active {
    background-color: #218838;
  }

  .my-wishlist-btn:active {
    background-color: #138496;
  }

  .logout-btn:active {
    background-color: #5a6268;
  }

  .retry-btn:active,
  .add-item-btn:active {
    background-color: #0056b3;
  }

  .back-btn:active {
    background-color: #e9ecef;
  }

  .close-btn:active {
    background-color: #e9ecef;
  }
}
</style>
